import React, { useState, useEffect, useCallback } from 'react'
import {
  Box, Paper, Typography, Skeleton, useTheme, alpha,
  TextField, InputAdornment, TablePagination, Drawer, IconButton,
} from '@mui/material'
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { analyticsApi } from '../../../../api/analyticsApi'

const UserTrackingTab = ({ period }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = theme.palette.primary.main

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage] = useState(50)

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [userEvents, setUserEvents] = useState([])
  const [eventsLoading, setEventsLoading] = useState(false)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const res = await analyticsApi.getUsers(period, search, page + 1, rowsPerPage)
      setData(res.data)
    } catch (err) {
      console.error('Failed to fetch users:', err)
    } finally {
      setLoading(false)
    }
  }, [period, search, page, rowsPerPage])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    setPage(0)
  }, [period, search])

  const handleRowClick = async (user) => {
    setSelectedUser(user)
    setDrawerOpen(true)
    setEventsLoading(true)
    try {
      const res = await analyticsApi.getUserEvents(user.user_id || user.id)
      setUserEvents(res.data?.events || res.data || [])
    } catch (err) {
      console.error('Failed to fetch user events:', err)
      setUserEvents([])
    } finally {
      setEventsLoading(false)
    }
  }

  const users = data?.users || data?.items || []
  const totalCount = data?.total || data?.count || users.length

  const headerCells = [
    { label: t('analytics_email'), flex: 2 },
    { label: t('analytics_name'), flex: 1.5 },
    { label: t('analytics_last_active'), width: 140 },
    { label: t('analytics_sessions'), width: 80, align: 'right' },
    { label: t('analytics_top_feature'), flex: 1 },
    { label: t('analytics_country'), width: 80 },
  ]

  return (
    <Box
      sx={{
        overflow: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      {/* Search */}
      <TextField
        size="small"
        placeholder={t('analytics_search_placeholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{
          mb: 2,
          width: { xs: '100%', sm: 360 },
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: '0.85rem',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} style={{ color: theme.palette.text.disabled }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Users Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            py: 1.2,
            px: 2,
            background: alpha(theme.palette.text.primary, 0.03),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {headerCells.map((cell) => (
            <Box
              key={cell.label}
              sx={{
                flex: cell.flex,
                width: cell.width,
                textAlign: cell.align || 'left',
                flexShrink: cell.width ? 0 : undefined,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  fontSize: '0.68rem',
                  letterSpacing: '0.05em',
                }}
              >
                {cell.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Table Body */}
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} variant="text" width="100%" height={40} sx={{ mb: 0.5 }} />
            ))}
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {t('analytics_no_users_found')}
            </Typography>
          </Box>
        ) : (
          users.map((user, idx) => (
            <Box
              key={user.user_id || user.id || idx}
              onClick={() => handleRowClick(user)}
              sx={{
                display: 'flex',
                gap: 1.5,
                py: 1.4,
                px: 2,
                cursor: 'pointer',
                borderBottom: idx < users.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
                transition: 'background 0.15s',
                '&:hover': { background: alpha(goldColor, 0.04) },
              }}
            >
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </Typography>
              </Box>
              <Box sx={{ flex: 1.5, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.name || user.full_name || '-'}
                </Typography>
              </Box>
              <Box sx={{ width: 140, flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>
                  {user.last_active ? new Date(user.last_active).toLocaleDateString() : '-'}
                </Typography>
              </Box>
              <Box sx={{ width: 80, flexShrink: 0, textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: theme.palette.text.primary }}>
                  {user.sessions ?? user.session_count ?? 0}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.top_feature || '-'}
                </Typography>
              </Box>
              <Box sx={{ width: 80, flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary }}>
                  {user.country || '-'}
                </Typography>
              </Box>
            </Box>
          ))
        )}

        {/* Pagination */}
        {!loading && totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[50]}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              '& .MuiTablePagination-toolbar': { minHeight: 48 },
            }}
          />
        )}
      </Paper>

      {/* User Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '100vw',
            p: 3,
            background: theme.palette.background.default,
          },
        }}
      >
        {selectedUser && (
          <>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedUser.name || selectedUser.full_name || selectedUser.email}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                  {selectedUser.email}
                </Typography>
                {selectedUser.country && (
                  <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                    {selectedUser.country}
                  </Typography>
                )}
              </Box>
              <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                <X size={18} />
              </IconButton>
            </Box>

            {/* User Stats */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 3,
                p: 2,
                borderRadius: '10px',
                background: alpha(goldColor, 0.05),
                border: `1px solid ${alpha(goldColor, 0.15)}`,
              }}
            >
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  {selectedUser.sessions ?? selectedUser.session_count ?? 0}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                  {t('analytics_sessions')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  {selectedUser.top_feature || '-'}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                  {t('analytics_top_feature')}
                </Typography>
              </Box>
            </Box>

            {/* Event Timeline */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {t('analytics_activity_timeline')}
            </Typography>

            {eventsLoading ? (
              <>
                {[...Array(6)].map((_, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <Skeleton variant="circular" width={12} height={12} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={18} />
                      <Skeleton variant="text" width="40%" height={14} />
                    </Box>
                  </Box>
                ))}
              </>
            ) : userEvents.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {t('analytics_no_events')}
              </Typography>
            ) : (
              <Box sx={{ position: 'relative' }}>
                {userEvents.map((event, idx) => {
                  const isLast = idx === userEvents.length - 1
                  return (
                    <Box key={idx} sx={{ display: 'flex', gap: 1.5, position: 'relative', pb: isLast ? 0 : 2 }}>
                      {/* Timeline line + dot */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 12, flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: alpha(goldColor, 0.6),
                            border: `2px solid ${goldColor}`,
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        />
                        {!isLast && (
                          <Box
                            sx={{
                              width: 1.5,
                              flex: 1,
                              background: alpha(theme.palette.divider, 0.6),
                              mt: 0.5,
                            }}
                          />
                        )}
                      </Box>

                      {/* Event content */}
                      <Box sx={{ flex: 1, minWidth: 0, pb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, color: theme.palette.text.primary }}>
                          {event.event_type || event.type}
                        </Typography>
                        {(event.category || event.action) && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '0.72rem' }}>
                            {[event.category, event.action].filter(Boolean).join(' / ')}
                          </Typography>
                        )}
                        {event.page_url && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {event.page_url}
                          </Typography>
                        )}
                        {event.timestamp && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.68rem' }}>
                            {new Date(event.timestamp).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </>
        )}
      </Drawer>
    </Box>
  )
}

export default UserTrackingTab
